import { useTranslation } from 'react-i18next';
import { annualize } from '../../domain/annualize';
import { Amount, CategoryDot, List, ListRow, Section, StatTile, series, useFormat } from '../../ui';
import type { BillsData } from './bills';

/**
 * A year of bills (V1.5 annualized subscriptions), under the bills of the
 * month: what the active bills cost a year, subscriptions (what could be
 * cancelled) apart from essentials, each bill's year most expensive first,
 * and what paused bills save.
 */
export function YearOfBills({ data, onEdit }: { data: BillsData; onEdit: (id: string) => void }) {
  const { t } = useTranslation();
  const { money } = useFormat();
  const whole = (minor: number) => money(minor, { whole: true });
  const year = annualize(data.templates, data.linked, data.categories);
  if (year.bills.length === 0) return null;
  const perPeriod = (b: (typeof year.bills)[number]) => t(`annual.per.${b.template.frequency}`, { amount: money(b.perPeriod!) });

  return (
    <Section title={t('annual.title')} data-testid="year-of-bills">
      <p className="annual-total">
        <span className="annual-total-label">{t('annual.total')}</span>
        <Amount minor={year.total} size="xl" whole />
        <span className="annual-total-month num">{t('annual.perMonth', { amount: whole(Math.round(year.total / 12)) })}</span>
      </p>
      <div className="stats annual-split" role="group" aria-label={t('annual.split')}>
        <StatTile label={t('annual.subscriptions', { count: year.subscriptions.count })} value={whole(year.subscriptions.total)} />
        <StatTile label={t('annual.essentials', { count: year.essentials.count })} value={whole(year.essentials.total)} />
      </div>
      <List label={t('annual.list')}>
        {year.bills.map((b) => (
          <ListRow
            key={b.template.id}
            density="compact"
            leading={<CategoryDot color={series(data.categories.get(b.template.categoryId)?.colorSlot ?? 0)} />}
            title={b.template.name}
            subtitle={
              b.perYear === null
                ? t('annual.noAmount')
                : `${b.estimate ? t('annual.about') + ' ' : ''}${perPeriod(b)}${b.subscription ? ' · ' + t('annual.subscriptionTag') : ''}`
            }
            trailing={b.perYear === null ? '–' : t('annual.perYear', { amount: whole(b.perYear) })}
            onClick={() => onEdit(b.template.id)}
          />
        ))}
      </List>
      {year.unknown > 0 && <p className="tone-muted section-note">{t('annual.unknown', { count: year.unknown })}</p>}
      {year.paused.count > 0 && (
        <p className="annual-paused num" data-testid="annual-paused">
          {t('annual.paused', { count: year.paused.count, amount: whole(year.paused.total) })}
        </p>
      )}
    </Section>
  );
}
